import { isOnline } from './api';

/**
 * API Helpers
 * دوال مساعدة للربط بين Zustand Stores و API
 *
 * Protocols Applied:
 * - P-API-001: RESTful API Integration
 * - P-OFFLINE-001: Offline-First Strategy
 * - P010: Error Handling
 * - P-DRY-001: Don't Repeat Yourself
 */

/**
 * Fetch All - جلب كل البيانات من API
 * @param {Function} apiMethod - API method to call (e.g., productsAPI.getAll)
 * @param {Function} setFn - Zustand set function
 * @param {Function} getFn - Zustand get function
 * @param {string} stateKey - State key name (e.g., 'products', 'customers')
 * @returns {Promise<Array>}
 */
export const fetchAll = async (apiMethod, setFn, getFn, stateKey) => {
  try {
    setFn({ loading: true, error: null });

    // Check if online
    if (!isOnline()) {
      throw new Error('لا يوجد اتصال بالإنترنت - استخدام البيانات المحلية');
    }

    const response = await apiMethod();
    const data = response.data.data || response.data;

    setFn({ [stateKey]: data, loading: false });

    return data;
  } catch (error) {
    setFn({ loading: false, error: error.message });
    // Keep local data if API fails
    console.warn(`Failed to fetch ${stateKey} from API, using local cache:`, error.message);
    return getFn()[stateKey];
  }
};

/**
 * Create - إنشاء عنصر جديد
 * @param {Function} apiMethod - API method to call (e.g., productsAPI.create)
 * @param {Object} data - Data to create
 * @param {Function} setFn - Zustand set function
 * @param {Function} getFn - Zustand get function
 * @param {string} stateKey - State key name (e.g., 'products', 'customers')
 * @param {Function} generateLocal - Function to generate local object
 * @returns {Promise<Object>}
 */
export const create = async (apiMethod, data, setFn, getFn, stateKey, generateLocal) => {
  try {
    setFn({ error: null, loading: true });

    let newItem;

    // Try API first if online
    if (isOnline()) {
      try {
        const response = await apiMethod(data);
        newItem = response.data.data || response.data;
      } catch (apiError) {
        // Check if it's a validation error (400, 422) - throw it up
        if (apiError.response && (apiError.response.status === 400 || apiError.response.status === 422)) {
          setFn({ loading: false });
          throw apiError; // Re-throw validation errors
        }

        console.warn('API create failed, falling back to local:', apiError.message);
        // Fallback to local creation for network errors only
        newItem = generateLocal(data);
      }
    } else {
      // Offline: Create locally
      newItem = generateLocal(data);
    }

    const state = getFn();
    setFn({
      [stateKey]: [...state[stateKey], newItem],
      loading: false
    });

    return newItem;
  } catch (error) {
    setFn({ error: error.message, loading: false });
    throw error;
  }
};

/**
 * Update - تحديث عنصر
 * @param {Function} apiMethod - API method to call (e.g., productsAPI.update)
 * @param {number} id - Item ID
 * @param {Object} updates - Updates to apply
 * @param {Function} setFn - Zustand set function
 * @param {Function} getFn - Zustand get function
 * @param {string} stateKey - State key name (e.g., 'products', 'customers')
 * @returns {Promise<Object>} { success: boolean, message?: string, error?: string }
 */
export const update = async (apiMethod, id, updates, setFn, getFn, stateKey) => {
  try {
    setFn({ error: null, loading: true });

    let apiResponse = null;

    // Try API first if online
    if (isOnline()) {
      try {
        apiResponse = await apiMethod(id, updates);
      } catch (apiError) {
        // Check if it's a validation error (400, 422) - throw it up
        if (apiError.response && (apiError.response.status === 400 || apiError.response.status === 422)) {
          setFn({ loading: false });
          return {
            success: false,
            error: apiError.response?.data?.message || apiError.message || 'فشل في التحديث'
          };
        }

        console.warn('API update failed, updating locally:', apiError.message);
      }
    }

    // Update locally
    const state = getFn();
    setFn({
      [stateKey]: state[stateKey].map((item) =>
        item.id === id ? { ...item, ...updates, updatedAt: new Date().toISOString() } : item
      ),
      loading: false
    });

    return {
      success: true,
      message: apiResponse?.data?.message || 'تم التحديث بنجاح'
    };
  } catch (error) {
    setFn({ error: error.message, loading: false });
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete - حذف عنصر
 * @param {Function} apiMethod - API method to call (e.g., productsAPI.delete)
 * @param {number} id - Item ID
 * @param {Function} setFn - Zustand set function
 * @param {Function} getFn - Zustand get function
 * @param {string} stateKey - State key name (e.g., 'products', 'customers')
 * @returns {Promise<Object>} { success: boolean, message?: string, error?: string }
 */
export const deleteItem = async (apiMethod, id, setFn, getFn, stateKey) => {
  try {
    setFn({ error: null, loading: true });

    let apiResponse = null;

    // Try API first if online
    if (isOnline()) {
      try {
        apiResponse = await apiMethod(id);
      } catch (apiError) {
        console.warn('API delete failed, deleting locally:', apiError.message);
        setFn({ loading: false });
        return {
          success: false,
          error: apiError.response?.data?.message || apiError.message || 'فشل في الحذف'
        };
      }
    }

    // Delete locally
    const state = getFn();
    setFn({
      [stateKey]: state[stateKey].filter((item) => item.id !== id),
      loading: false
    });

    return {
      success: true,
      message: apiResponse?.data?.message || 'تم الحذف بنجاح'
    };
  } catch (error) {
    setFn({ error: error.message, loading: false });
    return {
      success: false,
      error: error.message
    };
  }
};
